import React, {FC, useEffect, useRef, useState} from "react"
import moment from "moment"
import PropTypes from "prop-types"

import useAuth from "@irati/auth/helpers/auth/useAuth"
import {PointsRequestFilterType, PointsRequestStatus, RenewalQuoteStatus, ZuoraCurrentPlanWindow} from "@irati/types"
import Button, {IconTypes} from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {Card} from "@irati/ui/src/Layout"
import {Body, H4, Label} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import salesService from "@irati/sales/services/sales"
import buService from "@irati/billing/services/bu"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Icon from "@irati/ui/src/Icon"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Pill from "@irati/ui/src/Pill"
import usersService from "@irati/identity/services/users"
import adminTracking, {adminDashboardTracking} from "@irati-tracking/admin"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import workosService from "@irati/workos/services/workos"
import Loading from "@irati/ui/src/Loading"
import {hangPromise} from "@irati/utils/promise"
import History from "@irati/utils/history"
import {useListRequestPoints} from "@irati/wallet/hooks/wallet"
import {RenewalPlanSource} from "@irati-tracking/admin/admin"
import {useListOrdersQuery} from "@irati/payrollpay/hooks"

import SendInvitesModal from "./SendInvitesModal"
import {BILLING_TABS} from "../../containers/Billing"

interface PanelButtonProps {
  to: string
  cta: string
  app: string
  onClick: () => void
}

const PanelButton: FC<PanelButtonProps> = ({to, cta, app, onClick = () => {}}) => {
  const theme = useTheme()
  return (
    <ButtonRouterLink tertiary to={to} app={app} onClick={onClick}>
      <FormattedMessage id={cta} /> <Icon icon={IconTypes.chevronRight} color={theme.colors.primary1} />
    </ButtonRouterLink>
  )
}

interface SinglePanelProps {
  content: string
  children: any
  number?: number
}

const SinglePanel: FC<SinglePanelProps> = ({content, children, number}) => {
  const theme = useTheme()
  return (
    <Card padding={1}>
      <Flex alignItems="center" gap={1} justifyContent="space-between">
        <Flex gap={1} alignItems="center">
          {number ? (
            <Pill padding={theme.spacing.half} text={number} isWhiteText backgroundColor={theme.colors.tertiary.r_400} />
          ) : (
            <Icon icon={IconTypes.circleAlert} color={theme.colors.utility.warning} />
          )}
          <Body small maxWidth="100%">
            {content}
          </Body>
        </Flex>
        <div>{children}</div>
      </Flex>
    </Card>
  )
}

interface Props {}
interface Context {
  apps: {[key: string]: string}
}

const NotificationPanel: FC<Props> = (_, context: Context) => {
  const {apps} = context
  const theme = useTheme()
  const [listOrders, ordersData] = useListOrdersQuery({
    organisation_approval: "Pending"
  })

  const [pendingOrders, setPendingOrders] = useState(0)
  const [unActivatedUsers, setUnActivatedUsers] = useState(0)
  const [showReminderBanner, setShowReminderBanner] = useState(false)
  const [showUpcomingRenewalBanner, setShowUpcomingRenewalBanner] = useState(false)
  const [showEvergreenRenewalBanner, setShowEvergreenRenewalBanner] = useState(false)
  const [renewalDate, setRenewalDate] = useState(undefined)
  const [fileRecentlyUploaded, setFileRecentlyUploaded] = useState(true)
  const [load, setLoad] = useState<Promise<any>>(hangPromise())
  const [dsync, setDsync] = useState(false)
  const {user, business} = useAuth()
  const businessId = business.uuid
  const {data} = useListRequestPoints(
    {
      input: {
        status: PointsRequestStatus.StatusPending,
        idType: PointsRequestFilterType.TypeRequester,
        uuid: user?.uuid
      }
    },
    {
      fetchPolicy: "network-only"
    }
  )

  useEffect(() => {
    if (ordersData?.data?.orders?.length) {
      const totalPending = ordersData?.data?.scheme_types_by_count.find((tab) => tab.name.includes("All"))?.value
      setPendingOrders(totalPending)
    }
  }, [ordersData?.data])

  useEffect(() => {
    listOrders()
    const renewalLoad = Promise.all([buService.getCurrentPlan({businessId}), salesService.getRenewalQuoteStatus(businessId)]).then(
      ([{endDate, window, renewalPending}, quote]) => {
        const isRenewalWindowOpen =
          window === ZuoraCurrentPlanWindow.RenewalWindow ||
          window === ZuoraCurrentPlanWindow.RenewalWindowWithoutCancellation ||
          (window === ZuoraCurrentPlanWindow.Ongoing && renewalPending) ||
          false
        if (isRenewalWindowOpen) {
          if (quote?.sentForSignature?.seconds) {
            const is6HoursAfterSentForSignature = moment().isAfter(moment.unix(quote.sentForSignature.seconds).add(6, "hours"))
            if (quote.status === RenewalQuoteStatus.SentForSignature && is6HoursAfterSentForSignature) {
              setShowReminderBanner(true)
              setRenewalDate(moment(endDate).format("DD/MM/YYYY"))
            }
          } else if (quote?.status === RenewalQuoteStatus.Available && !quote.isManual) {
            setRenewalDate(moment(endDate).format("DD/MM/YYYY"))
            if (quote?.isEvergreen) {
              setShowEvergreenRenewalBanner(true)
            } else {
              setShowUpcomingRenewalBanner(true)
            }
          }
        }
      }
    )
    const unactivatedUsersLoad = usersService.getNonOnboardedUsersForBusiness({businessId}).then((res) => {
      if (res.total) {
        setUnActivatedUsers(res.total)
      }
    })
    const fileRecentlyUploadedLoad = usersService.getUsersForBusiness({businessId, sort: "updated_at"}).then((res) => {
      setFileRecentlyUploaded(moment(res?.results[0]?.metadata?.updatedAt).isAfter(moment().subtract(30, "days")))
    })
    const dsyncLoad = workosService.getOrganisation(businessId).then((organisation) => {
      setDsync(organisation?.directoryId)
    })
    const updatedLoad = Promise.allSettled([renewalLoad, unactivatedUsersLoad, fileRecentlyUploadedLoad, dsyncLoad])
    setLoad(updatedLoad)
  }, [])

  const handleCopyLink = () => {
    const link = `${window.location.origin}/activate?utm_source=admin_activation_invite&utm_medium=link`
    const copyText = formatMessage({id: "admin.components.dashboard.adminNotification.copyText"}, {link})
    navigator.clipboard.writeText(copyText).then(() => {
      triggerSuccessToast(formatMessage({id: "admin.components.dashboard.adminNotification.copyAlert"}))
    })
    adminTracking.ctaAdminActivationInvite()
    adminDashboardTracking.adminNotificationPanelActivateUsersShareLink()
  }

  const handleOpenModal = () => {
    adminDashboardTracking.adminNotificationPanelActivateUsersSendEmail()
    sendInvitesModal.current.show()
  }

  const handleRenewalBannerClick = () => {
    adminTracking.renewalPlan({action: RenewalPlanSource.reviewRenewalPlan})
    History.push({pathname: `/billing/${BILLING_TABS.NEW_PLAN}`, state: {source: RenewalPlanSource.reviewRenewalPlan}}, apps.ADMIN)
  }

  const handleEvergreenRenewalBannerClick = () => {
    adminDashboardTracking.adminNotificationPanelEvergreenRenewal()
    handleRenewalBannerClick()
  }

  const handleUpcomingRenewalBannerClick = () => {
    adminDashboardTracking.adminNotificationPanelUpcomingRenewal()
    handleRenewalBannerClick()
  }

  const handleReminderBannerClick = () => {
    adminDashboardTracking.adminNotificationPanelReminderRenewal()
    History.push({pathname: `/billing/${BILLING_TABS.RENEWAL_STATUS}`, state: {source: RenewalPlanSource.reviewRenewalStatus}}, apps.ADMIN)
  }

  const auth = useAuth()
  const sendInvitesModal = useRef()
  const {formatMessage} = useIntl()
  const isEmpty =
    !pendingOrders &&
    !showUpcomingRenewalBanner &&
    !showEvergreenRenewalBanner &&
    !showReminderBanner &&
    unActivatedUsers === 0 &&
    fileRecentlyUploaded &&
    data?.results?.length === 0 &&
    dsync

  return (
    <Loading load={load}>
      <Card bgColor={theme.colors.primary1_50} marginBottom={2}>
        <Flex gap={1} alignItems="center" marginBottom={2}>
          <Icon icon={IconTypes.checklist} />
          <H4>
            <FormattedMessage id="admin.components.notificationPanel.title" values={{name: auth.user?.firstName}} />
          </H4>
        </Flex>
        {isEmpty && (
          <Label>
            <FormattedMessage id="admin.components.notificationPanel.label" />
          </Label>
        )}
        <Flex direction="column" gap={1}>
          {showUpcomingRenewalBanner && (
            <SinglePanel content={formatMessage({id: "admin.components.notificationPanel.upcomingRenewalBanner"}, {renewalDate})}>
              <Button tertiary onClick={handleUpcomingRenewalBannerClick}>
                <FormattedMessage id="admin.components.notificationPanel.viewRenewalDetails" />{" "}
                <Icon marginLeft={0.5} icon={IconTypes.chevronRight} color={theme.colors.primary1} />
              </Button>
            </SinglePanel>
          )}
          {showEvergreenRenewalBanner && (
            <SinglePanel content={formatMessage({id: "platform.renewals.evergreenRenewalBanner.title"})}>
              <Button tertiary onClick={handleEvergreenRenewalBannerClick}>
                <FormattedMessage id="admin.components.notificationPanel.viewRenewalDetails" />{" "}
                <Icon marginLeft={0.5} icon={IconTypes.chevronRight} color={theme.colors.primary1} />
              </Button>
            </SinglePanel>
          )}
          {showReminderBanner && (
            <SinglePanel content={formatMessage({id: "platform.renewals.reminderBanner.text"}, {renewalDate})}>
              <Button tertiary onClick={handleReminderBannerClick}>
                <FormattedMessage id="admin.components.notificationPanel.viewRenewalStatus" />{" "}
                <Icon marginLeft={0.5} icon={IconTypes.chevronRight} color={theme.colors.primary1} />
              </Button>
            </SinglePanel>
          )}
          {!!pendingOrders && (
            <SinglePanel
              content={formatMessage({id: "admin.components.notificationPanel.salarySacrifice.content"})}
              number={pendingOrders}>
              <PanelButton
                cta="admin.components.notificationPanel.salarySacrifice.cta"
                to="/orderManagement"
                onClick={adminDashboardTracking.adminNotificationPanelOrderManagement}
              />
            </SinglePanel>
          )}
          {unActivatedUsers > 0 ? (
            <SinglePanel
              content={formatMessage({id: "admin.components.notificationPanel.unActivatedUsers.content"})}
              number={unActivatedUsers}>
              <Button tertiary marginRight onClick={handleCopyLink}>
                <FormattedMessage id="admin.components.notificationPanel.unActivatedUsers.shareLink" />
              </Button>{" "}
              |{" "}
              <Button tertiary marginLeft onClick={handleOpenModal}>
                <FormattedMessage id="admin.components.notificationPanel.unActivatedUsers.sendEmail" />
              </Button>
            </SinglePanel>
          ) : (
            !dsync && (
              <SinglePanel content={formatMessage({id: "admin.components.notificationPanel.unActivatedUsers.fallback.content"})}>
                <PanelButton
                  cta="admin.components.notificationPanel.unActivatedUsers.fallback.cta"
                  to="/people"
                  onClick={adminDashboardTracking.adminNotificationPanelActivateUsersFallback}
                />
              </SinglePanel>
            )
          )}
          {!fileRecentlyUploaded && !dsync && (
            <SinglePanel content={formatMessage({id: "admin.components.notificationPanel.fileRecentlyUploaded.content"})}>
              <PanelButton
                cta="admin.components.notificationPanel.fileRecentlyUploaded.cta"
                to="/people"
                onClick={adminDashboardTracking.adminNotificationPanelFileRecentlyUploaded}
              />
            </SinglePanel>
          )}
          {!!data?.results?.length && (
            <SinglePanel
              content={formatMessage({id: "admin.components.notificationPanel.rewardApprovals.content"})}
              number={data?.results?.length}>
              <PanelButton
                cta="admin.components.notificationPanel.rewardApprovals.cta"
                to="/wallet/flexi-reward"
                app={apps.WALLET}
                onClick={adminDashboardTracking.adminNotificationPanelRewardApprovals}
              />
            </SinglePanel>
          )}
        </Flex>
      </Card>
      <SendInvitesModal ref={sendInvitesModal} />
    </Loading>
  )
}

NotificationPanel.contextTypes = {
  apps: PropTypes.object.isRequired,
  appPath: PropTypes.string
}

export default NotificationPanel
